// Aide identity for evaluation visits (Supervisory Visit / Annual Performance
// Evaluation). On these visits the case's "caregiver" is the aide the nurse is
// there to evaluate, and she has to confirm she is with the RIGHT aide before
// she starts — so the visit screen and the evaluation's identity card both show
// the aide's name AND phone number. The number is the one field the server
// ships in the clear (visit.caregiverPhoneNumber); every other contact on the
// device is an opaque contactRef.

import { formatPhoneDisplay } from "@taskhealth/shared";

import type { FormDefinition } from "@/lib/api";

type VisitContact = Pick<
    FormDefinition["visit"],
    "caregiverName" | "caregiverPhoneNumber" | "caregiverContactRef" | "caregiverPhones"
>;

export interface AideContact {
    /** Display name; falls back to a placeholder when only a phone is on file. */
    name: string;
    /** True when the name came from the visit, not the placeholder. */
    hasName: boolean;
    /** Formatted number for display, or null when none is on file. */
    phoneDisplay: string | null;
    /** Opaque ref for the server-bridged call button, or null when uncallable. */
    contactRef: string | null;
    initials: string;
}

/** True when the form carries an aide-evaluation section — the same signal the
 *  section screen uses to pick SupervisoryStep / PerformanceEvaluationStep. */
export function isAideEvaluationVisit(
    phases: Array<{ sections: Array<{ behaviorType: string }> }>
): boolean {
    return phases.some((phase) =>
        phase.sections.some(
            (sec) =>
                sec.behaviorType === "supervisory_evaluation" ||
                sec.behaviorType === "performance_evaluation"
        )
    );
}

/** +12125551234 / 2125551234 → +1 (212) 555-1234. Anything that is not a
 *  10-digit NANP number (legacy imports carry free-text) is shown as stored
 *  rather than mangled into a wrong-looking number. */
export function formatAidePhone(raw: string): string {
    const digits = raw.replace(/\D/g, "");
    const national =
        digits.length === 10 ? digits : digits.length === 11 && digits[0] === "1" ? digits : null;
    return national ? formatPhoneDisplay(national) : raw.trim();
}

function normalizeName(name: string | null | undefined): string {
    return (name ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** The scheduled aide's phone belongs to the scheduled aide. Show it under the
 *  identity card only while the name on the card is still that aide's — once
 *  the nurse has typed a different person's name the number would mislead.
 *  Compares names, not the step's "renamed" flag: PerformanceEvaluationStep
 *  seeds its editable name from the schedule and so flags it renamed at once. */
export function showScheduledAidePhone(
    displayedName: string | null | undefined,
    scheduledName: string | null | undefined,
    scheduledPhone: string | null | undefined
): boolean {
    if (!scheduledPhone?.trim()) {
        return false;
    }
    const scheduled = normalizeName(scheduledName);
    return scheduled.length > 0 && normalizeName(displayedName) === scheduled;
}

export function initialsFrom(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    const first = parts[0][0] ?? "";
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
    return `${first}${last}`.toUpperCase();
}

/** null when the visit has neither an aide name nor a phone on file. */
export function buildAideContact(visit: VisitContact): AideContact | null {
    const name = visit.caregiverName?.trim() ?? "";
    const phone = visit.caregiverPhoneNumber?.trim() ?? "";
    if (!name && !phone) {
        return null;
    }
    const contactRef =
        visit.caregiverContactRef ??
        visit.caregiverPhones?.find((p) => p.isPrimary)?.contactRef ??
        visit.caregiverPhones?.[0]?.contactRef ??
        null;
    return {
        name: name || "Aide (name not on file)",
        hasName: name.length > 0,
        phoneDisplay: phone ? formatAidePhone(phone) : null,
        contactRef,
        initials: name ? initialsFrom(name) : "?"
    };
}
